import { Box, Grid, SimpleGrid, Text, Flex, Button, IconButton, useToken } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { FaUsers, FaUserPlus, FaShoppingCart, FaDollarSign, FaArrowLeft } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import MetricCard from '../MetricCard';
import DailyTransactionsChart from '../DailyTransactionsChart';
import MonthlyDepositsChart from '../MonthlyDepositsChart';
import MonthlyUserStatsChart from '../MonthlyUserStatsChart';
import MirrorStatisticsTable from '../MirrorStatisticsTable';
import { getUsersTotal, getMonthlyUserStats } from '../../../api/AdminAPI';
import {
  ADMIN_USERS_PATH,
  ADMIN_STARS_RATE_PATH,
  ADMIN_MIRROR_STARS_PATH,
} from 'router/adminPath';
import './DashboardOverview.css';

// The checker-accuracy dashboard lives in Grafana (owner decision 2026-09-13,
// spec 2026-09-11-getipintel-scoring-replica-phase2 slice F): the admin panel
// only links to it. No route, no API, rendered inside AdminGuard like the rest.
export const CHECKERS_DASHBOARD_URL =
  'https://grafana.agop.im/d/ipscore-checker-accuracy';

const DashboardOverview = () => {
  const [totalUsers, setTotalUsers] = useState<number>(0);
  const [monthlyStats, setMonthlyStats] = useState({
    newUsers: 0,
    transactions: 0,
    deposits: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const token = useSelector((state: RootState) => state.userData.apiKey);
  const navigate = useNavigate();
  // MetricCard paints its icon tile through `style`, so it needs resolved
  // values (`var(--chakra-colors-chart-N)`), never a token name.
  const [chart1, chart2, chart3, chart4] = useToken('colors', [
    'chart.1',
    'chart.2',
    'chart.3',
    'chart.4',
  ]);

  useEffect(() => {
    const fetchData = async () => {
      if (!token) return;

      setIsLoading(true);
      setError(null);

      try {
        // Get total number of users
        const totalResponse = await getUsersTotal(token);
        
        if (totalResponse.success && totalResponse.data) {
          setTotalUsers(totalResponse.data.count);
        }

        // Get statistics for the last month
        const statsResponse = await getMonthlyUserStats(token, 1, 0, true);
        
        if (statsResponse.success && statsResponse.data && statsResponse.data.length > 0) {
          const lastMonth = statsResponse.data[0];
          setMonthlyStats({
            newUsers: lastMonth.new_users,
            transactions: lastMonth.tx_count,
            deposits: lastMonth.usd_count,
          });
        } else {
          setError(statsResponse.error || 'Failed to load monthly stats');
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [token]);

  if (error) {
    return (
      <Box p={4} borderRadius="md" bg="status.errorSubtle" color="status.error">
        <Text>Error loading data: {error}</Text>
      </Box>
    );
  }

  return (
    <Box className="dashboard-overview">
      <Flex justify="space-between" align="center" mb={6} flexDir={{ base: 'column', md: 'row' }} gap={{ base: 4, md: 0 }}>
        <Flex align="center" gap={4}>
          <IconButton
            aria-label="Back"
            icon={<FaArrowLeft />}
            onClick={() => navigate('/')}
            variant="ghost"
            size="lg"
          />
          <Box textAlign={{ base: 'center', md: 'left' }}>
            <Text fontSize={{ base: '2xl', md: '3xl' }} fontWeight="bold">
              Admin Dashboard
            </Text>
            <Text color="text.secondary" mt={1} fontSize={{ base: 'sm', md: 'md' }}>
              Welcome to IP Score management panel
            </Text>
          </Box>
        </Flex>
        
        <Flex gap={2}>
          <Button variant="outline" size="sm" onClick={() => navigate(ADMIN_USERS_PATH)}>
            User Management
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate(ADMIN_STARS_RATE_PATH)}>
            Stars Rate
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate(ADMIN_MIRROR_STARS_PATH)}>
            Bot Stars
          </Button>
          <Button
            as="a"
            href={CHECKERS_DASHBOARD_URL}
            target="_blank"
            rel="noopener noreferrer"
            variant="outline"
            size="sm"
          >
            Checkers (Grafana)
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate('/profile/info')}>
            Profile
          </Button>
        </Flex>
      </Flex>

      {/* Metrics Cards */}
      <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={6} mb={8}>
        <MetricCard
          title="Total Users"
          value={totalUsers.toLocaleString()}
          icon={<FaUsers />}
          iconColor={chart1}
          isLoading={isLoading}
        />
        <MetricCard
          title="New Users"
          value={monthlyStats.newUsers.toLocaleString()}
          icon={<FaUserPlus />}
          iconColor={chart2}
          isLoading={isLoading}
        />
        <MetricCard
          title="Transactions"
          value={monthlyStats.transactions.toLocaleString()}
          icon={<FaShoppingCart />}
          iconColor={chart3}
          isLoading={isLoading}
        />
        <MetricCard
          title="Deposits"
          value={`$${monthlyStats.deposits.toLocaleString()}`}
          icon={<FaDollarSign />}
          iconColor={chart4}
          isLoading={isLoading}
        />
      </SimpleGrid>

      {/* Charts */}
      <Grid
        templateColumns={{ base: '1fr', xl: '1fr 1fr' }}
        gap={6}
        mb={8}
      >
        <DailyTransactionsChart />
        <MonthlyDepositsChart />
      </Grid>

      {/* Full Width Chart */}
      <Box mb={8}>
        <MonthlyUserStatsChart />
      </Box>

      {/* Mirror Statistics Table */}
      <Box mb={8}>
        <MirrorStatisticsTable />
      </Box>
    </Box>
  );
};

export default DashboardOverview;

